import type { CampaignResponse } from '@/api/campaignPlaner/fetchCampaign';
import { buildOptimisationRules } from '@/dashboards/CampaignOptimization/core/buildOptimisationRules';
import { channelBounds, type ChannelBounds } from '@/dashboards/CampaignOptimization/core/channelBounds';
import type { MediaLimit } from '@/dashboards/shared/optimisation/optimisationSession';
import { useCampaignDashboardData, type CampaignDashboardData } from '@/dashboards/CampaignOptimization/core/useCampaignDashboardData';
import { useCampaignHistoricalData } from '@/dashboards/CampaignOptimization/core/useCampaignHistoricalData';
import { useOptimizationSessionState, type UseOptimizationSessionStateReturn } from '@/dashboards/CampaignOptimization/core/useOptimizationSessionState';
import { useCampaignOptimizationUrl } from '@/dashboards/CampaignOptimization/hooks/useCampaignOptimizationUrl';
import { useDisabledChannels } from '@/dashboards/CampaignOptimization/hooks/useDisabledChannels';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import type { OptimisationBase } from '@/optimizer';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { calculateCampaignDateConstraints } from '@/utils/campaignDateConstraints';
import type { Message } from '@analytical-alley/opt-wasm';
import moment from 'moment';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createContext } from 'use-context-selector';

export type OptimizationSessionContextValue = UseOptimizationSessionStateReturn & {
    campaign: CampaignResponse;
    canEdit: boolean;
    optimizationBase: OptimisationBase;
    channelBounds: ChannelBounds;
    disabledChannels: string[];
    dateConstraints: { minDate: string; maxDate: string; visibleMinDate: Date; visibleMaxDate: Date };
    messages: Message[];
    flushMessages: () => void;
    totalContributionGraphPeriod: 'monthly' | 'weekly';
    setTotalContributionGraphPeriod: (value: 'monthly' | 'weekly') => void;
    data: CampaignDashboardData;
    isDataLoading: boolean;
    isDataFetching: boolean;
};

export const OptimizationSessionContext = createContext<OptimizationSessionContextValue | null>(null);

const EMPTY_MESSAGES: Message[] = [];

const toDateParam = (value: string | Date | null | undefined) => (value ? moment(value).format('DD-MM-YYYY') : '');

export const OptimizationSessionProvider = ({ campaign, children }: { campaign: CampaignResponse; children: ReactNode }) => {
    const { optimizer, optimizerReady, rules: configRules, setDashboardType } = useOptimizer();
    const { search, setSearch } = useCampaignOptimizationUrl();
    const { dashboard, variableNameMap } = useDashboardGlobalContext();
    const disabledChannels = useDisabledChannels(campaign);

    useEffect(() => {
        setDashboardType('campaign');
    }, [setDashboardType]);

    const dateConstraints = useMemo(() => {
        const c = calculateCampaignDateConstraints(toDateParam(dashboard.modelStartDate), toDateParam(dashboard.modelEndDate));
        return {
            minDate: moment(c.selectableMinDate).format('DD-MM-YYYY'),
            maxDate: moment(c.selectableMaxDate).format('DD-MM-YYYY'),
            visibleMinDate: c.visibleMinDate,
            visibleMaxDate: c.visibleMaxDate,
        };
    }, [dashboard.modelStartDate, dashboard.modelEndDate]);

    const canEdit = campaign.status === 'draft';

    const session = useOptimizationSessionState({
        campaign,
        configRules,
        optimizer,
        optimizerReady,
        search: {
            budget: search.budget,
            startDate: search.startDate,
            endDate: search.endDate,
            optimizationBase: search.optimizationBase,
        },
        setSearch,
        defaults: {
            campaignDefaultBudget: dashboard.campaignDefaultBudget,
            campaignDefaultStartDate: dashboard.campaignDefaultStartDate,
            campaignDefaultEndDate: dashboard.campaignDefaultEndDate,
        },
        minDate: dateConstraints.minDate,
        maxDate: dateConstraints.maxDate,
        canEdit,
    });

    const { draft, committed } = session;
    const boundsDates = draft ?? committed?.inputs;
    const bounds = useMemo<ChannelBounds>(() => {
        if (!boundsDates) return {};
        const rules = buildOptimisationRules({
            configRules,
            campaign,
            startDate: boundsDates.startDate,
            endDate: boundsDates.endDate,
            lockedChannels: [],
        });
        return channelBounds({
            baseLimits: optimizer.getBaseProjectMediaLimits() as MediaLimit[],
            startDate: boundsDates.startDate,
            endDate: boundsDates.endDate,
            rules,
            result: committed?.result ?? null,
        });
    }, [boundsDates?.startDate, boundsDates?.endDate, configRules, campaign, optimizer, committed]);

    const [totalContributionGraphPeriod, setTotalContributionGraphPeriod] = useState<'monthly' | 'weekly'>('weekly');
    const historical = useCampaignHistoricalData({
        startDate: committed?.inputs.startDate,
        endDate: committed?.inputs.endDate,
        enabled: committed !== null,
        graphPeriod: totalContributionGraphPeriod,
    });
    const data = useCampaignDashboardData({
        result: committed?.result ?? null,
        previous: historical.previous,
        variableNameMap,
        contributionLastYear: historical.contributionLastYear,
    });

    const [dismissedRunAt, setDismissedRunAt] = useState<number | null>(null);
    const messages = committed && committed.finishedAt !== dismissedRunAt ? committed.messages : EMPTY_MESSAGES;
    const flushMessages = useCallback(() => setDismissedRunAt(committed?.finishedAt ?? null), [committed?.finishedAt]);

    const value = useMemo<OptimizationSessionContextValue>(
        () => ({
            ...session,
            campaign,
            canEdit,
            optimizationBase: draft?.optimizationBase ?? committed?.inputs.optimizationBase ?? search.optimizationBase ?? 'budget',
            channelBounds: bounds,
            disabledChannels,
            dateConstraints,
            messages,
            flushMessages,
            totalContributionGraphPeriod,
            setTotalContributionGraphPeriod,
            data,
            isDataLoading: historical.isPreviousLoading,
            isDataFetching: historical.isPreviousFetching,
        }),
        [
            session,
            campaign,
            canEdit,
            draft,
            committed,
            search.optimizationBase,
            bounds,
            disabledChannels,
            dateConstraints,
            messages,
            flushMessages,
            totalContributionGraphPeriod,
            data,
            historical.isPreviousLoading,
            historical.isPreviousFetching,
        ],
    );

    return <OptimizationSessionContext.Provider value={value}>{children}</OptimizationSessionContext.Provider>;
};
